// @ts-check

/**
 * @file Renders a paginated async task select.
 */

import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import {memo} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"

import ModelSelect from "../model-select"
import {Task} from "../../frontend-models/task"
import {MODEL_SELECT_PAGE_SIZE, modelSelectOptionsResult, normalizedSelectValues} from "../model-select-options"
import LocalTask from "../../models/task"
import {PROJECT_REPLICA_DATABASE_IDENTIFIER} from "../../database/project-replica"
import TaskPickerSearch, {taskSearchTerms} from "./task-search"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @import {ModelSelectOptionsResult} from "../model-select" */
/** @import {OpenProjectReplica} from "../../database/project-replica.js" */

/**
 * @typedef {object} TasksSelectProps
 * @property {string | number | null} [defaultValue] Initial selected task id.
 * @property {(string | number)[]} [defaultValues] Initial selected task ids.
 * @property {boolean} [includeProjectName] Label options with the project name and search across task name, project name, task id and project id.
 * @property {string} [label] Select label.
 * @property {boolean} [multiple] Allow multiple selections.
 * @property {boolean} [online] Whether remote task queries are available.
 * @property {import("../../database/project-replica.js").OpenProjectReplica | null} [replica] Open project replica for offline task picks.
 * @property {string} [name] Api Maker form field name.
 * @property {(...args: unknown[]) => unknown} [onChangeValue] Selection change handler.
 * @property {string} [placeholder] Placeholder text.
 * @property {string | number | null} [projectId] Optional project id filter.
 * @property {string} testID Stable test id.
 * @property {(string | number)[]} [values] Controlled selected values.
 */

/** @typedef {Record<string, never>} TasksSelectState */

/**
 * Task select that searches and paginates task labels server-side.
 * @augments {ShapeComponent<TasksSelectProps, TasksSelectState>}
 */
class TasksSelect extends ShapeComponent {
  static propTypes = propTypesExact({
    defaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    defaultValues: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
    ),
    includeProjectName: PropTypes.bool,
    label: PropTypes.string,
    multiple: PropTypes.bool,
    name: PropTypes.string,
    onChangeValue: PropTypes.func,
    placeholder: PropTypes.string,
    projectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    testID: PropTypes.string.isRequired,
    values: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
    )
  })
  static defaultProps = {
    online: true,
    replica: null,
    defaultValue: undefined,
    defaultValues: undefined,
    includeProjectName: false,
    label: undefined,
    multiple: false,
    name: undefined,
    onChangeValue: undefined,
    placeholder: undefined,
    projectId: undefined,
    values: undefined
  }

  /** @type {TaskPickerSearch | undefined} */
  searchHelper = undefined

  /** Initializes locale support. */
  setup() {
    useLocale()
  }

  /**
   * Renders the select.
   * @returns {ReactNode} Task select.
   */
  render() {
    const {
      defaultValue,
      defaultValues,
      label,
      multiple,
      name,
      onChangeValue,
      placeholder,
      testID,
      values
    } = this.p
    return (
      <ModelSelect
        defaultValue={defaultValue}
        defaultValues={defaultValues}
        label={label}
        multiple={multiple}
        name={name}
        onChangeValue={onChangeValue}
        options={this.tt.loadOptions}
        placeholder={placeholder || _("Search tasks...")}
        testID={testID}
        values={values}
      />
    )
  }

  /**
   * Loads task options for HayaSelect.
   * @param {{page?: number, searchValue?: string, values?: (string | number)[] | string | number}} args Loader args.
   * @returns {Promise<ModelSelectOptionsResult>} Paginated options.
   */
  loadOptions = async ({page = 1, searchValue, values} = {}) => {
    if (!this.p.online && this.p.replica) {
      return this.loadOfflineOptions({page, searchValue, values})
    }
    const selectedValues = normalizedSelectValues(values)
    if (selectedValues.length > 0) {
      const tasks = /** @type {InstanceType<typeof Task>[]} */ (
        /** @type {unknown} */ (
          await this.applySelect(
            Task
              .where({id: selectedValues})
              .order([["name", "asc"], ["id", "asc"]])
          ).toArray()
        )
      )
      return modelSelectOptionsResult({
        options: tasks.map((task) => this.taskToOption(task)),
        page: 1,
        totalCount: tasks.length
      })
    }

    /** @type {Record<string, unknown>} */
    const ransackParams = {}
    if (searchValue) {
      if (this.p.includeProjectName) {
        Object.assign(ransackParams, await this.search().paramsFor({searchValue}))
      } else {
        ransackParams.nameCont = searchValue
      }
    }
    if (this.p.projectId != null && String(this.p.projectId).length > 0) {
      ransackParams.projectIdEq = this.p.projectId
    }
    const baseQuery = Task.ransack(ransackParams)
    const [tasks, totalCount] = await Promise.all([
      /** @type {Promise<InstanceType<typeof Task>[]>} */ (
        /** @type {unknown} */ (
          this
            .applySelect(
              baseQuery
                .clone()
                .order([["name", "asc"], ["id", "asc"]])
                .page(page)
                .perPage(MODEL_SELECT_PAGE_SIZE)
            )
            .toArray()
        )
      ),
      baseQuery.clone().count()
    ])
    return modelSelectOptionsResult({
      options: tasks.map((task) => this.taskToOption(task)),
      page,
      totalCount
    })
  }

  /**
   * Loads task options from the local replica so the picker works offline.
   *
   * A replica holds exactly one project and stores no project name, so offline
   * searches match task names, task ids and project ids only; the project-name
   * match is a server query.
   * @param {{page: number, searchValue?: string, values?: (string | number)[] | string | number}} args Loader args.
   * @returns {Promise<ModelSelectOptionsResult>} Options read from the local replica.
   */
  async loadOfflineOptions({page, searchValue, values}) {
    const replica = this.p.replica
    if (!replica) {
      return modelSelectOptionsResult({options: [], page: 1, totalCount: 0})
    }
    const selectedValues = normalizedSelectValues(values)
    const terms = taskSearchTerms(searchValue)
    const rows = await replica.handle.databaseOperation({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER}, async (operation) => {
      return await operation.forModel(LocalTask).order("name").order("id").toArray()
    })
    const options = rows
      .filter((task) => selectedValues.length === 0 || selectedValues.includes(String(task.id())))
      .filter((task) => terms.every((term) => this.offlineTaskMatchesTerm(task, term)))
      .map((task) => ({
        text: task.name() || String(task.id()),
        value: String(task.id())
      }))
    return modelSelectOptionsResult({
      options,
      page: 1,
      totalCount: options.length
    })
  }

  /**
   * Checks one offline search word against a task name, task id or project id.
   * @param {InstanceType<typeof LocalTask>} task Replica task row.
   * @param {string} term Search word.
   * @returns {boolean} Whether this replica row matches the word.
   */
  offlineTaskMatchesTerm(task, term) {
    const needle = term.toLowerCase()

    return String(task.name() || "").toLowerCase().includes(needle) ||
      String(task.id()).toLowerCase() === needle ||
      String(task.projectId() || "").toLowerCase() === needle
  }

  /**
   * Returns the per-component search helper, so project-name lookups are cached.
   * @returns {TaskPickerSearch} Search helper for this picker instance.
   */
  search() {
    this.searchHelper ||= new TaskPickerSearch()

    return this.searchHelper
  }

  /**
   * Applies the minimal select/preload needed for task labels.
   * @param {ReturnType<typeof Task.query>} query Task query.
   * @returns {ReturnType<typeof Task.query>} Query with selected attributes.
   */
  applySelect(query) {
    if (this.p.includeProjectName) {
      return query
        .select({
          Project: ["id", "name"],
          Task: ["id", "name", "projectId"]
        })
        .preload({project: true})
    }
    return query.select(["id", "name"])
  }

  /**
   * Converts a task model into a select option.
   * @param {InstanceType<typeof Task>} task Task model.
   * @returns {{text: string, value: string}} Text/value pair using the optional project label.
   */
  taskToOption(task) {
    const taskName = task.name() || String(task.id())
    const project = this.p.includeProjectName ? task.project() : null
    const projectName = project ? project.name() : null
    return {
      text: projectName ? `${taskName} — ${projectName}` : taskName,
      value: String(task.id())
    }
  }
}

/** @type {NamedExoticComponent<TasksSelectProps>} */
const TasksSelectComponent = memo(shapeComponent(TasksSelect))

export default TasksSelectComponent
